import { useEffect, useRef, useState } from 'react'
import { Copy, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { parseGermanNumber } from '@/lib/utils'

export type InlineFieldType = 'text' | 'number' | 'currency' | 'date' | 'select' | 'textarea'

interface Props {
  label: string
  field: string
  /** Rohwert aus der API — wird beim Bearbeiten in die Eingabe uebernommen. */
  value: string | number | null | undefined
  type?: InlineFieldType
  options?: string[]
  /** Anzeige-Darstellung des Rohwerts (Waehrung, Datum). Default: String(value). */
  display?: (value: any) => React.ReactNode
  mono?: boolean
  /** Wenn gesetzt, erscheint rechts ein Kopier-Knopf. */
  copyValue?: string | null
  placeholder?: string
  readOnly?: boolean
  /** Anzeigename je Options-Wert — fuer Selects, deren Werte Schluessel sind. */
  optionLabel?: (value: string) => string
  /** Breite der Label-Spalte in px. 0 blendet sie aus (z.B. Titel in einer Kopfleiste). */
  labelWidth?: number
  /** Zusatzklassen fuer Anzeige und Eingabe — etwa groessere Schrift im Kopf. */
  className?: string
  onSave: (field: string, value: string | number | null) => Promise<unknown>
}

/**
 * Klick-zum-Bearbeiten-Feld.
 *
 * Enter (bzw. Verlassen) speichert, Escape verwirft. Gespeichert wird nur, wenn sich
 * der Wert tatsaechlich geaendert hat — sonst wuerde jedes versehentliche Antippen
 * einen PUT und einen History-Eintrag erzeugen.
 */
export default function InlineField({
  label, field, value, type = 'text', options, display, mono,
  copyValue, placeholder = 'Klicken…', readOnly, optionLabel, labelWidth = 104,
  className = '', onSave,
}: Props) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null)
  // Verhindert, dass blur nach Escape oder nach Enter ein zweites Mal speichert.
  const settled = useRef(false)

  useEffect(() => {
    if (!editing) return
    const el = inputRef.current
    el?.focus()
    if (el && 'select' in el && type !== 'date') el.select()
  }, [editing, type])

  const begin = () => {
    if (readOnly || saving) return
    settled.current = false
    setDraft(value == null ? '' : String(value))
    setEditing(true)
  }

  const commit = async (save: boolean) => {
    if (settled.current) return
    settled.current = true

    if (!save) { setEditing(false); return }

    const raw = draft.trim()
    let next: string | number | null

    if (type === 'number' || type === 'currency') {
      next = raw === '' ? null : parseGermanNumber(raw)
      if (raw !== '' && next === null) {
        toast.error(`${label}: „${raw}" ist keine gültige Zahl`)
        setEditing(false)
        return
      }
    } else {
      next = raw === '' ? null : raw
    }

    // Unveraendert → kein Request
    const before = value == null || value === '' ? null : value
    if (String(before ?? '') === String(next ?? '')) { setEditing(false); return }

    setEditing(false)
    setSaving(true)
    try {
      await onSave(field, next)
    } finally {
      setSaving(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); void commit(false) }
    if (e.key === 'Enter' && type !== 'textarea') { e.preventDefault(); void commit(true) }
  }

  const inputCls =
    'w-full rounded-md border border-primary-500 bg-white px-1.5 py-0.5 text-sm text-gray-900 shadow-[0_0_0_3px_rgba(30,75,142,0.15)] focus:outline-none'

  return (
    <div
      className={`grid min-h-[30px] items-center gap-2 px-3 py-1 ${
        labelWidth > 0 ? 'border-t border-gray-100 first:border-t-0' : 'px-0'
      }`}
      style={{ gridTemplateColumns: `${labelWidth > 0 ? `${labelWidth}px ` : ''}minmax(0,1fr) auto` }}
    >
      {labelWidth > 0 && <label className="text-[11px] text-gray-400">{label}</label>}

      <div className="min-w-0">
        {editing ? (
          type === 'select' ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => void commit(true)}
              onKeyDown={onKeyDown}
              className={inputCls}
            >
              <option value="">—</option>
              {options?.map((o) => (
                <option key={o} value={o}>{optionLabel ? optionLabel(o) : o}</option>
              ))}
            </select>
          ) : type === 'textarea' ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={draft}
              rows={3}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => void commit(true)}
              onKeyDown={onKeyDown}
              className={`${inputCls} resize-y`}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={type === 'date' ? 'date' : 'text'}
              inputMode={type === 'number' || type === 'currency' ? 'decimal' : undefined}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => void commit(true)}
              onKeyDown={onKeyDown}
              className={`${inputCls} ${mono ? 'font-mono text-xs' : ''} ${className}`}
            />
          )
        ) : (
          <button
            type="button"
            onClick={begin}
            disabled={readOnly || saving}
            title={readOnly ? undefined : 'Klicken zum Bearbeiten'}
            className={`group -mx-1.5 flex w-full items-center gap-1.5 truncate rounded-md border border-transparent px-1.5 py-0.5 text-left text-sm transition-colors ${
              readOnly ? 'cursor-default' : 'cursor-text hover:border-gray-200 hover:bg-gray-50'
            } ${mono ? 'font-mono text-xs' : ''} ${className} ${value == null || value === '' ? 'italic text-gray-400' : 'text-gray-900'}`}
          >
            <span className="truncate">
              {value == null || value === ''
                ? placeholder
                : display ? display(value)
                : optionLabel ? optionLabel(String(value))
                : String(value)}
            </span>
            {saving && <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin text-gray-400" />}
            {!readOnly && !saving && (
              <span className="ml-auto flex-shrink-0 text-[11px] text-gray-300 opacity-0 transition-opacity group-hover:opacity-100">✎</span>
            )}
          </button>
        )}
      </div>

      {copyValue ? (
        <button
          type="button"
          title="Kopieren"
          onClick={() => { navigator.clipboard?.writeText(copyValue); toast.success('Kopiert') }}
          className="rounded p-1 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      ) : <span />}
    </div>
  )
}
